import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { IMAGES } from "@/data/site";
import { Zap, ShieldCheck, Timer, Droplets, Smartphone, ArrowRight } from "lucide-react";

const LINES = [
  { text: "LA RIVOLUZIONE", cls: "text-white" },
  { text: "AEREA PER I TUOI", cls: "text-stroke-fire" },
  { text: "PANNELLI SOLARI", cls: "fire-text" },
];

const CHIPS = [
  { icon: Zap, value: "+35%", label: "Resa Energetica", pos: "-left-4 sm:-left-10 top-10", delay: "0s" },
  { icon: Timer, value: "45 min", label: "Impianto 6 kW", pos: "-right-3 sm:-right-8 top-1/3", delay: "1.2s" },
  { icon: ShieldCheck, value: "0", label: "Rischi sul Tetto", pos: "-left-3 sm:-left-8 bottom-24", delay: "0.6s" },
  { icon: Droplets, value: "100%", label: "Acqua Osmotizzata", pos: "right-6 -bottom-5", delay: "1.8s" },
];

const STATS = [
  { value: "3–60 kW", label: "Impianti Residenziali" },
  { value: "5x", label: "Più Veloce" },
  { value: "100%", label: "Pagamento Sicuro" },
  { value: "ENAC", label: "Piloti Certificati" },
];

function MaskedLine({ text, cls, index }) {
  return (
    <div className="overflow-hidden py-[0.06em]">
      <motion.div
        initial={{ y: "112%" }}
        animate={{ y: 0 }}
        transition={{ duration: 1, delay: 0.25 + index * 0.14, ease: [0.16, 1, 0.3, 1] }}
        className={`font-display font-bold uppercase leading-[0.95] tracking-tight text-[13.5vw] sm:text-7xl lg:text-[5.2rem] xl:text-[6rem] ${cls}`}
        data-testid={`hero-headline-line-${index}`}
      >
        {text}
      </motion.div>
    </div>
  );
}

export function Hero({ onBook }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  return (
    <section id="top" ref={ref} className="relative min-h-screen flex flex-col justify-center overflow-hidden pt-28 pb-10" data-testid="hero-section">
      <div
        className="absolute -top-40 -left-40 w-[560px] h-[560px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(255,107,0,0.13) 0%, transparent 65%)" }}
      />
      <div
        className="absolute top-1/3 -right-52 w-[640px] h-[640px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(255,46,0,0.09) 0%, transparent 65%)" }}
      />

      <div className="max-w-7xl mx-auto px-5 sm:px-8 w-full grid lg:grid-cols-12 gap-12 lg:gap-8 items-center relative z-10">
        <div className="lg:col-span-7">
          <div className="overflow-hidden mb-6">
            <motion.p
              initial={{ y: "120%" }}
              animate={{ y: 0 }}
              transition={{ duration: 0.9, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
              className="font-mono-adj text-[11px] sm:text-xs uppercase tracking-[0.35em] text-orange-500 font-semibold"
              data-testid="hero-eyebrow"
            >
              ADJ AirDroneJet — Pulizia Fotovoltaico con Drone
            </motion.p>
          </div>

          <h1 className="select-none">
            {LINES.map((l, i) => (
              <MaskedLine key={l.text} text={l.text} cls={l.cls} index={i} />
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.75, ease: [0.16, 1, 0.3, 1] }}
            className="text-base sm:text-lg text-slate-400 leading-relaxed max-w-xl mt-7"
            data-testid="hero-subtitle"
          >
            Pulizia professionale con drone per impianti fotovoltaici da 3 a 60 kW.
            Recupera fino al +35% di resa energetica: zero ponteggi, zero rischi sul tetto.
            Paghi online, anche a rate, o come accordiamo insieme.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="flex flex-wrap items-center gap-4 mt-9"
          >
            <button
              onClick={() => onBook(null)}
              data-testid="hero-book-cta-button"
              className="fire-bg text-white font-mono-adj text-xs uppercase tracking-[0.2em] font-semibold rounded-full px-8 py-4 hover:scale-[1.04] active:scale-95 transition-transform duration-300 flex items-center gap-2"
            >
              Prenota Intervento <ArrowRight size={15} />
            </button>
            <a
              href="#come-funziona"
              data-testid="hero-discover-cta-link"
              className="border border-white/20 text-slate-200 font-mono-adj text-xs uppercase tracking-[0.2em] rounded-full px-8 py-4 hover:border-orange-500/70 hover:text-orange-400 transition-colors duration-300"
            >
              Scopri la Tecnologia
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.1 }}
            className="flex items-center gap-2 mt-8 text-slate-500"
            data-testid="hero-app-badge"
          >
            <Smartphone size={15} className="text-orange-500" />
            <span className="font-mono-adj text-[11px] uppercase tracking-[0.2em]">
              App iOS / Android in arrivo — <a href="#app" className="text-orange-400 underline underline-offset-4 decoration-orange-500/40 hover:decoration-orange-400">entra in waitlist</a>
            </span>
          </motion.div>
        </div>

        <div className="lg:col-span-5 relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.94, y: 40 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="relative"
          >
            <div className="rounded-[2rem] overflow-hidden border border-white/10 relative" data-testid="hero-image-frame">
              <motion.img
                src={IMAGES.hero}
                alt="Drone pulisce pannelli fotovoltaici al tramonto"
                style={{ y: imgY, scale: 1.15 }}
                className="w-full h-[420px] sm:h-[520px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#050507]/70 via-transparent to-transparent" />
              <p className="absolute bottom-5 left-6 font-mono-adj text-[10px] uppercase tracking-[0.3em] text-orange-400/90">
                Unità ADJ-01 — In Azione
              </p>
            </div>

            {CHIPS.map((c) => (
              <div
                key={c.label}
                className={`float-chip absolute ${c.pos} glass border border-white/10 rounded-2xl px-4 py-3 flex items-center gap-3 shadow-2xl`}
                style={{ animationDelay: c.delay }}
                data-testid={`hero-stat-chip-${c.label.toLowerCase().replace(/\s/g, "-")}`}
              >
                <c.icon size={18} className="text-orange-500 shrink-0" />
                <div>
                  <p className="font-heading font-bold text-white text-sm leading-none">{c.value}</p>
                  <p className="font-mono-adj text-[9px] uppercase tracking-[0.18em] text-slate-400 mt-1">{c.label}</p>
                </div>
              </div>
            ))}
          </motion.div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1, delay: 1.25 }}
        className="max-w-7xl mx-auto px-5 sm:px-8 w-full mt-16 relative z-10"
      >
        <div className="grid grid-cols-2 md:grid-cols-4 border-y border-white/10 divide-x divide-white/10" data-testid="hero-stats-ribbon">
          {STATS.map((s) => (
            <div key={s.label} className="py-6 px-4 text-center">
              <p className="font-display font-bold text-3xl sm:text-4xl fire-text">{s.value}</p>
              <p className="font-mono-adj text-[10px] uppercase tracking-[0.2em] text-slate-500 mt-2">{s.label}</p>
            </div>
          ))}
        </div>
      </motion.div>
    </section>
  );
}
